import React, { useMemo, useState } from "react";
import { MdLaptopMac, MdPhoneIphone, MdVisibilityOff } from "react-icons/md";

import placeholderImage from "../assets/img/menuImage.png";

/**
 * ProductSimulation — "how will this actually look to a customer?"
 *
 * Every dimension, radius, colour and font size below is copied from the real
 * client code, not eyeballed, so this is a preview rather than an impression:
 *
 *   brand / web    Lusuku_Web  src/pages/BrandShopPage.js  (ProductCard :1921,
 *                  ProductImageWrap :1940, ProductName :1998, SkuLine :2011,
 *                  ProductPrice :2095, grid minmax(280px) :1910)
 *   brand / mobile Lusuku_Expo_Clean  app/screens/BrandShopScreen.js
 *                  (productCardShadow/productCard/productImage :664-730,
 *                  2-up grid at (screenWidth - 36) / 2, image height 160)
 *   rw / web       Lusuku_Web  src/pages/VendorShopPage.js  (ProductCard :1218,
 *                  ProductImageWrapper :1681 height 220, ProductName :1252,
 *                  ProductPrice :1269, ProductUnits :1449)
 *   rw / mobile    Lusuku_Expo_Clean  app/screens/RetailerMenuScreen.js
 *                  (one-column list, square full-bleed image, :542-694)
 *
 * Currency is formatted the way EACH surface formats it — the web clients call
 * Intl with UGX (Chrome renders that as "USh"), the brand app hardcodes
 * "UGX 12,000" and the retailer app hardcodes "Shs 12,000". Showing one
 * invented format everywhere would hide a real inconsistency between them.
 *
 * DISABLED PRODUCTS ARE EXCLUDED, because the storefronts exclude them
 * (BrandShopPage.js:684, VendorShopPage.js:651). The previous inline simulation
 * on the vendor menu showed them, so it advertised products no shopper could
 * ever see. A counter reports how many were withheld instead.
 */

const PHONE_W = 390; // iPhone 14 logical width — the basis for the 2-up card math
const PHONE_H = 720; // trimmed from 844 so the frame fits a dashboard panel

const fmtWeb = (n) => {
  try {
    return new Intl.NumberFormat("en-UG", {
      style: "currency",
      currency: "UGX",
    }).format(Number(n) || 0);
  } catch (_) {
    return `UGX ${Number(n) || 0}`;
  }
};
const fmtBrandApp = (n) =>
  n == null ? "—" : `UGX ${Number(n).toLocaleString("en-UG")}`;
const fmtRwApp = (n) => `Shs ${Number(n || 0).toLocaleString("en-US")}`;

// Descriptions are rich HTML for RW products and plain-ish for brand ones; the
// mobile apps strip tags before rendering (BrandShopScreen.js:47).
const stripHtml = (html) =>
  !html ? "" : String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const ProductSimulation = ({
  sections = [],
  variant = "brand", // "brand" | "rw"
  storeName = "",
}) => {
  const [surface, setSurface] = useState("web"); // "web" | "mobile"

  const { visibleSections, hiddenCount, totalCount } = useMemo(() => {
    let hidden = 0;
    let total = 0;
    const out = [];
    (sections || []).forEach((section) => {
      const products = Array.isArray(section.products) ? section.products : [];
      total += products.length;
      const live = products.filter((p) => p.enabled);
      hidden += products.length - live.length;
      if (live.length) out.push({ ...section, products: live });
    });
    return { visibleSections: out, hiddenCount: hidden, totalCount: total };
  }, [sections]);

  const isBrand = variant === "brand";

  return (
    <div className="psim">
      <style>{CSS}</style>

      <div className="psim-bar">
        <div className="psim-toggle" role="group" aria-label="Preview surface">
          <button
            type="button"
            className={surface === "web" ? "is-on" : ""}
            onClick={() => setSurface("web")}
          >
            <MdLaptopMac size={16} /> Web storefront
          </button>
          <button
            type="button"
            className={surface === "mobile" ? "is-on" : ""}
            onClick={() => setSurface("mobile")}
          >
            <MdPhoneIphone size={16} /> Mobile app
          </button>
        </div>
        <p className="psim-note">
          {surface === "web"
            ? "Card sizes, spacing and type are taken from the live storefront code."
            : "Rendered at 390pt — an iPhone 14 — using the app's own card metrics."}
        </p>
      </div>

      {hiddenCount > 0 && (
        <div className="psim-hidden">
          <MdVisibilityOff size={15} />
          <span>
            {hiddenCount} of {totalCount} product{totalCount === 1 ? "" : "s"}{" "}
            {hiddenCount === 1 ? "is" : "are"} deactivated and hidden from
            customers, so {hiddenCount === 1 ? "it is" : "they are"} not shown
            here either.
          </span>
        </div>
      )}

      {!visibleSections.length ? (
        <div className="psim-empty">
          Nothing to preview yet — a section needs at least one active product
          before customers see anything.
        </div>
      ) : surface === "web" ? (
        <div className="psim-browser">
          <div className="psim-chrome">
            <span className="psim-dot" />
            <span className="psim-dot" />
            <span className="psim-dot" />
            <span className="psim-url">lusuku.shop</span>
          </div>
          <div className="psim-page">
            {!!storeName && <h2 className="psim-store">{storeName}</h2>}
            {visibleSections.map((section) => (
              <div key={section._id} className="psim-sec">
                <h3 className="psim-sec-title">{section.title}</h3>
                {!!section.description && (
                  <p className="psim-sec-desc">{stripHtml(section.description)}</p>
                )}
                <div className={isBrand ? "psim-grid-brand" : "psim-grid-rw"}>
                  {section.products.map((p) =>
                    isBrand ? (
                      <WebBrandCard key={p._id} p={p} />
                    ) : (
                      <WebRwCard key={p._id} p={p} />
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="psim-phone-wrap">
          <div className="psim-phone">
            <div className="psim-notch" />
            <div className="psim-screen">
              {!!storeName && <div className="psim-app-bar">{storeName}</div>}
              {visibleSections.map((section) => (
                <div key={section._id} className="psim-app-sec">
                  <div className="psim-app-sec-title">{section.title}</div>
                  {!!section.description && (
                    <div className="psim-app-sec-desc">
                      {stripHtml(section.description)}
                    </div>
                  )}
                  {isBrand ? (
                    <div className="psim-app-grid">
                      {section.products.map((p) => (
                        <AppBrandCard key={p._id} p={p} />
                      ))}
                    </div>
                  ) : (
                    section.products.map((p) => <AppRwCard key={p._id} p={p} />)
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Cards ────────────────────────────────────────────────────────────────── */

const img = (p) => p.image || placeholderImage;

const WebBrandCard = ({ p }) => (
  <div className="psim-wb">
    <div className="psim-wb-img">
      <img src={img(p)} alt={p.name} />
    </div>
    <div className="psim-wb-body">
      <div className="psim-wb-name">{p.name}</div>
      {!!p.skuCode && <div className="psim-wb-sku">{p.skuCode}</div>}
      {!!p.description && (
        <div className="psim-wb-desc">{stripHtml(p.description)}</div>
      )}
      <div className="psim-wb-price">
        {p.priceOnRequest ? "Price on request" : fmtWeb(p.price)}
      </div>
    </div>
  </div>
);

const WebRwCard = ({ p }) => (
  <div className="psim-wr">
    <div className="psim-wr-img">
      <img src={img(p)} alt={p.name} />
    </div>
    <div className="psim-wr-body">
      <div className="psim-wr-name">{p.name}</div>
      <div className="psim-wr-price">{fmtWeb(p.price)}</div>
      {!!p.description && (
        <div className="psim-wr-desc">{stripHtml(p.description)}</div>
      )}
      {!!(p.measurements || p.unit_name) && (
        <span className="psim-wr-unit">
          {p.measurements} {p.unit_name || p.unit || ""}
        </span>
      )}
    </div>
  </div>
);

const AppBrandCard = ({ p }) => (
  <div className="psim-ab">
    <div className="psim-ab-img">
      <img src={img(p)} alt={p.name} />
    </div>
    <div className="psim-ab-body">
      <div className="psim-ab-name">{p.name}</div>
      {!!p.description && (
        <div className="psim-ab-desc">{stripHtml(p.description)}</div>
      )}
      <div className="psim-ab-price">{fmtBrandApp(p.price)}</div>
    </div>
  </div>
);

const AppRwCard = ({ p }) => (
  <div className="psim-ar">
    <div className="psim-ar-img">
      <img src={img(p)} alt={p.name} />
    </div>
    <div className="psim-ar-body">
      <div className="psim-ar-top">
        <span className="psim-ar-name">{p.name}</span>
        {!!p.measurements && (
          <span className="psim-ar-meas">
            {p.measurements}
            <span className="psim-ar-unit">{p.unit_name || p.unit || "Pc"}</span>
          </span>
        )}
      </div>
      <div className="psim-ar-price">{fmtRwApp(p.price)}</div>
      {!!p.description && (
        <div className="psim-ar-desc">{stripHtml(p.description)}</div>
      )}
      <div className="psim-ar-stock">In Stock: {p.countInStock ?? 0}</div>
      <div className="psim-ar-cta">Add to cart</div>
    </div>
  </div>
);

/* ── Styles ───────────────────────────────────────────────────────────────── */
const CSS = `
.psim { --psim-ink:#1a1a2e; }
.psim-bar { display:flex; align-items:center; justify-content:space-between;
  gap:14px; flex-wrap:wrap; margin-bottom:14px; }
.psim-toggle { display:inline-flex; background:#eef1f4; border-radius:10px; padding:3px; }
.psim-toggle button { border:0; background:transparent; border-radius:8px;
  padding:7px 14px; font-size:0.82rem; font-weight:600; color:#5a6b7b;
  display:inline-flex; align-items:center; gap:7px; cursor:pointer; }
.psim-toggle button.is-on { background:#fff; color:#17385a;
  box-shadow:0 1px 4px rgba(15,23,42,0.14); }
.psim-note { margin:0; font-size:0.76rem; color:#94a3b8; }
.psim-hidden { display:flex; align-items:flex-start; gap:8px; font-size:0.78rem;
  color:#8a6d1f; background:#fdf6e3; border:1px solid #f0e2b6;
  border-radius:9px; padding:8px 12px; margin-bottom:14px; }
.psim-empty { text-align:center; color:#94a3b8; font-size:0.86rem; padding:44px 20px;
  border:1px dashed #d8dee4; border-radius:12px; }

/* Browser shell */
.psim-browser { border:1px solid #e2e8f0; border-radius:12px; overflow:hidden;
  box-shadow:0 4px 18px rgba(15,23,42,0.08); background:#fff; }
.psim-chrome { display:flex; align-items:center; gap:6px; padding:9px 14px;
  background:#f1f5f9; border-bottom:1px solid #e2e8f0; }
.psim-dot { width:9px; height:9px; border-radius:50%; background:#cbd5e1; }
.psim-url { margin-left:12px; font-size:0.72rem; color:#94a3b8; background:#fff;
  border-radius:20px; padding:3px 14px; border:1px solid #e2e8f0; }
.psim-page { padding:22px; background:#fff; }
.psim-store { font-size:1.15rem; font-weight:800; color:var(--psim-ink); margin:0 0 18px; }
.psim-sec { margin-bottom:30px; }
.psim-sec:last-child { margin-bottom:0; }
.psim-sec-title { font-size:1rem; font-weight:700; color:var(--psim-ink); margin:0 0 3px; }
.psim-sec-desc { font-size:0.8rem; color:#64748b; margin:0 0 14px; }

/* Web / brand — BrandShopPage.js */
.psim-grid-brand { display:grid; grid-template-columns:repeat(auto-fill, minmax(180px, 1fr)); gap:1.25rem; }
.psim-wb { background:#fff; border:1px solid #f1f5f9; border-radius:14px;
  overflow:hidden; box-shadow:0 2px 10px rgba(0,0,0,0.07); display:flex; flex-direction:column; }
.psim-wb-img { position:relative; width:100%; aspect-ratio:1/1; background:#f8fafc; }
.psim-wb-img img { width:100%; height:100%; object-fit:contain; padding:14px;
  box-sizing:border-box; display:block; }
.psim-wb-body { padding:1rem; display:flex; flex-direction:column; flex:1 1 auto; }
.psim-wb-name { color:#1a1a2e; font-size:1rem; font-weight:700; line-height:1.35;
  display:-webkit-box; -webkit-line-clamp:2; line-clamp:2; -webkit-box-orient:vertical;
  overflow:hidden; margin-bottom:2px; }
.psim-wb-sku { font-size:0.72rem; color:#94a3b8; letter-spacing:0.04em;
  font-family:monospace; margin-bottom:6px; }
.psim-wb-desc { font-size:0.88rem; color:#64748b; line-height:1.45;
  display:-webkit-box; -webkit-line-clamp:3; line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
.psim-wb-price { font-size:1.1rem; font-weight:800; color:#324d54; margin-top:auto; padding-top:10px; }

/* Web / retailer-wholesaler — VendorShopPage.js */
.psim-grid-rw { display:grid; grid-template-columns:repeat(auto-fill, minmax(170px, 1fr)); gap:20px; }
.psim-wr { background:#fff; border:1px solid #eaeaea; border-radius:12px;
  overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,0.06); display:flex; flex-direction:column; }
.psim-wr-img { height:170px; padding:10px; border-bottom:1px solid #eee; box-sizing:border-box; }
.psim-wr-img img { width:100%; height:100%; object-fit:contain; display:block; }
.psim-wr-body { padding:14px 16px; display:flex; flex-direction:column; gap:6px; }
.psim-wr-name { font-size:15px; font-weight:600; color:#1a1a1a; line-height:1.35;
  display:-webkit-box; -webkit-line-clamp:2; line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.psim-wr-price { font-size:15px; font-weight:700; color:#2d8a2d; }
.psim-wr-desc { font-size:12.5px; color:#888; line-height:1.5;
  display:-webkit-box; -webkit-line-clamp:2; line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.psim-wr-unit { align-self:flex-start; font-size:11px; font-weight:500; color:#666;
  padding:3px 10px; border-radius:12px; background:#f5f5f5; border:1px solid #e8e8e8; }

/* Phone shell */
.psim-phone-wrap { display:flex; justify-content:center; padding:8px 0 4px; }
.psim-phone { width:${PHONE_W + 20}px; height:${PHONE_H + 20}px; background:#111827;
  border-radius:44px; padding:10px; box-shadow:0 18px 44px rgba(15,23,42,0.28);
  position:relative; box-sizing:content-box; }
.psim-notch { position:absolute; top:16px; left:50%; transform:translateX(-50%);
  width:118px; height:24px; background:#111827; border-radius:14px; z-index:2; }
.psim-screen { width:${PHONE_W}px; height:${PHONE_H}px; background:#fff;
  border-radius:34px; overflow-y:auto; overflow-x:hidden; padding-top:44px; }
.psim-screen::-webkit-scrollbar { width:0; }
.psim-app-bar { font-size:17px; font-weight:700; color:#111827; padding:4px 16px 12px; }
.psim-app-sec { margin-bottom:18px; }
.psim-app-sec-title { font-size:17px; font-weight:700; color:#111827; padding:0 12px; }
.psim-app-sec-desc { font-size:13px; color:#6B7280; margin-top:2px; padding:0 12px 10px; }

/* App / brand — BrandShopScreen.js, 2-up at (390 - 36) / 2 = 177px */
.psim-app-grid { display:flex; flex-wrap:wrap; gap:12px; padding:0 12px; }
.psim-ab { width:177px; border-radius:14px; background:#fff; overflow:hidden;
  box-shadow:0 4px 10px rgba(107,33,168,0.18); }
.psim-ab-img { height:160px; background:#f5f3ff; }
.psim-ab-img img { width:100%; height:100%; object-fit:contain; display:block; }
.psim-ab-body { padding:10px; display:flex; flex-direction:column; gap:3px; }
.psim-ab-name { font-size:13px; font-weight:600; color:#111827;
  display:-webkit-box; -webkit-line-clamp:2; line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.psim-ab-desc { font-size:11px; color:#6B7280; line-height:16px;
  display:-webkit-box; -webkit-line-clamp:2; line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.psim-ab-price { font-size:14px; font-weight:700; color:#8B5CF6; margin-top:2px; }

/* App / retailer-wholesaler — RetailerMenuScreen.js, one column, square image */
.psim-ar { background:#fff; border-radius:10px; margin:8px 15px; overflow:hidden;
  box-shadow:0 2px 6px rgba(15,23,42,0.16); }
.psim-ar-img { width:100%; aspect-ratio:1/1; }
.psim-ar-img img { width:100%; height:100%; object-fit:contain; display:block; }
.psim-ar-body { padding:10px 12px 14px; }
.psim-ar-top { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.psim-ar-name { font-size:16px; font-weight:700; color:#0c0c0c; }
.psim-ar-meas { display:inline-flex; align-items:center; gap:6px;
  font-size:14px; font-weight:700; color:#CF0045; }
.psim-ar-unit { width:30px; height:30px; border-radius:15px; background:#f8f4f4;
  display:inline-flex; align-items:center; justify-content:center;
  font-size:12px; font-weight:400; color:#6e6969; box-shadow:0 1px 3px rgba(0,0,0,0.18); }
.psim-ar-price { font-size:16px; font-weight:700; color:#4ecdc4; margin-top:6px; }
.psim-ar-desc { font-size:14px; color:#6e6969; margin-top:4px; }
.psim-ar-stock { font-size:14px; color:#1C9111; margin-top:4px; }
.psim-ar-cta { margin-top:10px; background:#fc5c65; color:#fff; font-weight:700;
  font-size:16px; text-align:center; padding:15px; border-radius:6px; }
`;

export default ProductSimulation;
